"use client";

import { c as _c } from "react/compiler-runtime";
import { CompactModeProvider } from "@/contexts/CompactModeContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiWave } from "@/generated/models/ApiWave";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { Transition } from "@headlessui/react";
import { ArrowLeftIcon, ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";
import React, { Fragment, useCallback, useEffect, useState } from "react";
import { SingleWaveDropChat } from "./SingleWaveDropChat";
interface SingleWaveDropWrapperProps {
  readonly drop: ApiDrop;
  readonly wave: ApiWave;
  readonly children: React.ReactNode;
  readonly onClose: () => void;
}
export const SingleWaveDropWrapper: React.FC<SingleWaveDropWrapperProps> = t0 => {
  const $ = _c(47);
  const {
    drop,
    wave,
    children,
    onClose
  } = t0;
  const [isChatOpen, setIsChatOpen] = useState(false);
  const isSmallScreen = useMediaQuery("(max-width: 1023px)");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setIsChatOpen(_temp);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const toggleChat = t1;
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const browserWindow = globalThis.window;
      if (browserWindow === undefined) {
        return;
      }
      const handleClose = () => setIsChatOpen(false);
      browserWindow.addEventListener("single-drop:close-chat", handleClose);
      return () => browserWindow.removeEventListener("single-drop:close-chat", handleClose);
    };
    t3 = [];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[3] !== isChatOpen || $[4] !== isSmallScreen) {
    t4 = () => {
      if (!isChatOpen || !isSmallScreen) {
        return;
      }
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    };
    t5 = [isChatOpen, isSmallScreen];
    $[3] = isChatOpen;
    $[4] = isSmallScreen;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <ArrowLeftIcon className="tw-w-5 tw-h-5 sm:tw-w-4 sm:tw-h-4 tw-flex-shrink-0" />;
    t7 = <span className="tw-hidden sm:tw-inline tw-text-sm tw-font-semibold">Close</span>;
    $[7] = t6;
    $[8] = t7;
  } else {
    t6 = $[7];
    t7 = $[8];
  }
  let t8;
  if ($[9] !== onClose) {
    t8 = <div className="tw-flex tw-items-center tw-gap-6"><button type="button" aria-label="Close panel" onClick={onClose} className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-h-11 tw-w-11 sm:tw-h-auto sm:tw-w-auto sm:tw-px-3 sm:tw-py-2 tw-rounded-full sm:tw-rounded-lg tw-text-iron-300 desktop-hover:hover:tw-text-white tw-transition-all tw-bg-iron-950 sm:tw-bg-transparent tw-border tw-border-solid tw-border-iron-700 sm:tw-border-0">{t6}{t7}</button></div>;
    $[9] = onClose;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const t9 = isChatOpen ? "Hide chat" : "Show chat";
  const t10 = `tw-gap-2 tw-px-3 tw-h-11 sm:tw-h-auto sm:tw-py-2 tw-w-11 sm:tw-w-auto tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center tw-backdrop-blur-md tw-rounded-full sm:tw-rounded-lg tw-transition-all tw-text-sm tw-font-medium tw-border tw-border-solid ${isChatOpen ? "tw-bg-iron-800 tw-border-iron-700 tw-text-iron-100 desktop-hover:hover:tw-bg-iron-700" : "tw-bg-iron-950 tw-border-iron-700 tw-text-iron-300 desktop-hover:hover:tw-bg-iron-800 desktop-hover:hover:tw-text-iron-200"}`;
  let t11;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <ChatBubbleLeftRightIcon className="tw-w-5 tw-h-5 sm:tw-w-4 sm:tw-h-4 tw-flex-shrink-0" />;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  const t12 = isChatOpen ? "Hide" : "Show";
  let t13;
  if ($[12] !== t12) {
    t13 = <span className="tw-hidden sm:tw-inline">{t12} Chat</span>;
    $[12] = t12;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  let t14;
  if ($[14] !== t10 || $[15] !== t13 || $[16] !== t9) {
    t14 = <button onClick={toggleChat} aria-label={t9} className={t10}>{t11}{t13}</button>;
    $[14] = t10;
    $[15] = t13;
    $[16] = t9;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== t14 || $[19] !== t8) {
    t15 = <header className="tw-absolute tw-top-0 tw-left-0 tw-right-3 tw-z-20 tw-pt-[calc(env(safe-area-inset-top,0px)+0.5rem)] tw-pb-2 tw-px-2 lg:tw-px-8 @[640px]:tw-px-8 tw-bg-iron-950/55 tw-bg-gradient-to-b tw-from-iron-950/75 tw-via-iron-950/55 tw-to-iron-950/10 tw-backdrop-blur-sm"><div className="tw-w-full tw-flex tw-items-center tw-justify-between">{t8}{t14}</div></header>;
    $[18] = t14;
    $[19] = t8;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="tw-shrink-0 tw-h-[calc(env(safe-area-inset-top,0px)+4rem)]" aria-hidden="true" />;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="tw-shrink-0 tw-h-[env(safe-area-inset-bottom,0px)]" aria-hidden="true" />;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  let t18;
  if ($[23] !== children) {
    t18 = <div className="tw-h-full tw-overflow-y-auto tw-[scrollbar-gutter:stable] tw-scrollbar-thin tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 desktop-hover:hover:tw-scrollbar-thumb-iron-300">{t16}{children}{t17}</div>;
    $[23] = children;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] !== t15 || $[26] !== t18) {
    t19 = <div className="@container tw-flex-1 tw-relative tw-h-full tw-overflow-hidden">{t15}{t18}</div>;
    $[25] = t15;
    $[26] = t18;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  const t20 = `
            tw-hidden lg:tw-flex tw-flex-col tw-h-full tw-bg-black tw-transition-all tw-duration-300 tw-ease-in-out
            ${isChatOpen ? "tw-w-[400px] tw-border-l tw-border-solid tw-border-white/10 tw-border-y-0 tw-border-r-0" : "tw-w-0 tw-overflow-hidden tw-border-none"}
          `;
  let t21;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <div className="tw-absolute tw-top-0 tw-left-0 tw-right-0 tw-h-12 tw-bg-gradient-to-b tw-from-black tw-to-transparent tw-z-50 tw-pointer-events-none" />;
    $[28] = t21;
  } else {
    t21 = $[28];
  }
  let t22;
  if ($[29] !== drop || $[30] !== wave) {
    t22 = <div className="@container tw-flex tw-flex-col tw-flex-1 tw-overflow-hidden tw-relative">{t21}<SingleWaveDropChat key={drop.id} wave={wave} drop={drop} /></div>;
    $[29] = drop;
    $[30] = wave;
    $[31] = t22;
  } else {
    t22 = $[31];
  }
  let t23;
  if ($[32] !== t20 || $[33] !== t22) {
    t23 = <CompactModeProvider compact={true}><div className={t20}>{t22}</div></CompactModeProvider>;
    $[32] = t20;
    $[33] = t22;
    $[34] = t23;
  } else {
    t23 = $[34];
  }
  let t24;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <Transition.Child as={Fragment} enter="tw-ease-out tw-duration-150" enterFrom="tw-opacity-0" enterTo="tw-opacity-100" leave="tw-ease-in tw-duration-120" leaveFrom="tw-opacity-100" leaveTo="tw-opacity-0"><div className="tw-absolute tw-inset-0 tw-bg-black/60" onClick={toggleChat} /></Transition.Child>;
    $[35] = t24;
  } else {
    t24 = $[35];
  }
  let t25;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <div className="tw-flex tw-items-center tw-px-2 lg:tw-px-8 tw-pt-[calc(env(safe-area-inset-top,0px)+0.75rem)] tw-pb-2"><button onClick={toggleChat} className="tw-flex tw-items-center tw-rounded-lg tw-gap-2 tw-px-3 tw-py-2 tw-text-white/70 desktop-hover:hover:tw-text-white tw-transition-colors tw-bg-transparent tw-border-0"><ArrowLeftIcon className="tw-w-5 tw-h-5 tw-flex-shrink-0" /><span className="tw-text-sm tw-font-semibold">Close</span></button></div>;
    $[36] = t25;
  } else {
    t25 = $[36];
  }
  let t26;
  if ($[37] !== drop || $[38] !== wave) {
    t26 = <div className="lg:tw-hidden tw-fixed tw-inset-y-0 tw-right-0 tw-left-[var(--left-rail,0px)] tw-z-[90] tw-overflow-hidden">{t24}<Transition.Child as={Fragment} enter="tw-transform tw-transition tw-ease-out tw-duration-220" enterFrom="tw-translate-x-full" enterTo="tw-translate-x-0" leave="tw-transform tw-transition tw-ease-in tw-duration-180" leaveFrom="tw-translate-x-0" leaveTo="tw-translate-x-full"><div className="@container lg:tw-hidden tw-flex tw-flex-col tw-h-full tw-bg-iron-950 tw-absolute tw-inset-0 tw-z-[100] tw-transform-gpu tw-will-change-transform">{t25}<div className="@container tw-flex tw-flex-col tw-flex-1 tw-overflow-hidden"><SingleWaveDropChat key={drop.id} wave={wave} drop={drop} /></div></div></Transition.Child></div>;
    $[37] = drop;
    $[38] = wave;
    $[39] = t26;
  } else {
    t26 = $[39];
  }
  let t27;
  if ($[40] !== isChatOpen || $[41] !== t26) {
    t27 = <CompactModeProvider compact={true}><Transition.Root show={isChatOpen} as={Fragment}>{t26}</Transition.Root></CompactModeProvider>;
    $[40] = isChatOpen;
    $[41] = t26;
    $[42] = t27;
  } else {
    t27 = $[42];
  }
  let t28;
  if ($[43] !== t19 || $[44] !== t23 || $[45] !== t27) {
    t28 = <div className="tw-w-full tw-h-full tw-bg-iron-950 tw-flex tw-flex-col lg:tw-flex-row tw-overflow-hidden">{t19}{t23}{t27}</div>;
    $[43] = t19;
    $[44] = t23;
    $[45] = t27;
    $[46] = t28;
  } else {
    t28 = $[46];
  }
  return t28;
};
function _temp(prev) {
  return !prev;
}